import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TimeInput from '../components/TimeInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Soft Dig Data Sheet Form
function SoftDigForm({ jobs, onGenerate }) {
  const [form, setForm] = useState({
    job_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    location: '',
    depth: '',
    soil_type: '',
    utilities_found: [],
    photos_taken: false,
    ground_restored: false,
    notes: '',
    technician: ''
  });

  const utilityTypes = ['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Fiber', 'Unknown'];

  const handleUtilityToggle = (utility) => {
    setForm(prev => ({
      ...prev,
      utilities_found: prev.utilities_found.includes(utility)
        ? prev.utilities_found.filter(u => u !== utility)
        : [...prev.utilities_found, utility]
    }));
  };

  const handleSubmit = () => {
    if (!form.job_id || !form.location) {
      toast.error('Please fill required fields');
      return;
    }
    const selectedJob = jobs.find(j => j.id === form.job_id);
    onGenerate({ ...form, job: selectedJob });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Job *</Label>
          <Select value={form.job_id} onValueChange={(v) => setForm(p => ({ ...p, job_id: v }))}>
            <SelectTrigger><SelectValue placeholder="Select job" /></SelectTrigger>
            <SelectContent>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>{job.job_title} - {job.client_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Dig Location *</Label>
        <Input value={form.location} onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g., 10ft N of meter, at stake #3" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Depth Reached</Label>
          <Input value={form.depth} onChange={(e) => setForm(p => ({ ...p, depth: e.target.value }))} placeholder="e.g., 4.5 ft" />
        </div>
        <div className="space-y-2">
          <Label>Soil Type</Label>
          <Select value={form.soil_type} onValueChange={(v) => setForm(p => ({ ...p, soil_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Clay">Clay</SelectItem>
              <SelectItem value="Sand">Sand</SelectItem>
              <SelectItem value="Gravel">Gravel</SelectItem>
              <SelectItem value="Loam">Loam</SelectItem>
              <SelectItem value="Rocky">Rocky</SelectItem>
              <SelectItem value="Mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Utilities Found</Label>
        <div className="flex flex-wrap gap-3">
          {utilityTypes.map(util => (
            <label key={util} className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.utilities_found.includes(util)} onCheckedChange={() => handleUtilityToggle(util)} />
              <span className="text-sm">{util}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.photos_taken} onCheckedChange={(c) => setForm(p => ({ ...p, photos_taken: c }))} />
          <span className="text-sm">Photos Taken</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.ground_restored} onCheckedChange={(c) => setForm(p => ({ ...p, ground_restored: c }))} />
          <span className="text-sm">Ground Restored</span>
        </label>
      </div>

      <div className="space-y-2">
        <Label>Technician Name</Label>
        <Input value={form.technician} onChange={(e) => setForm(p => ({ ...p, technician: e.target.value }))} />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
      </div>

      <Button onClick={handleSubmit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900">
        <Download className="w-4 h-4 mr-2" />Generate Soft Dig Data Sheet
      </Button>
    </div>
  );
}

// PreCon Form
function PreConForm({ jobs, onGenerate }) {
  const [form, setForm] = useState({
    job_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    project_name: '',
    contractor: '',
    contact_phone: '',
    scope_of_work: '',
    utility_conflicts: '',
    access_issues: '',
    safety_concerns: '',
    special_requirements: '',
    attendees: '',
    notes: ''
  });

  const handleSubmit = () => {
    if (!form.job_id || !form.project_name) {
      toast.error('Please fill required fields');
      return;
    }
    const selectedJob = jobs.find(j => j.id === form.job_id);
    onGenerate({ ...form, job: selectedJob });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Job *</Label>
          <Select value={form.job_id} onValueChange={(v) => setForm(p => ({ ...p, job_id: v }))}>
            <SelectTrigger><SelectValue placeholder="Select job" /></SelectTrigger>
            <SelectContent>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>{job.job_title} - {job.client_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Project Name *</Label>
        <Input value={form.project_name} onChange={(e) => setForm(p => ({ ...p, project_name: e.target.value }))} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contractor</Label>
          <Input value={form.contractor} onChange={(e) => setForm(p => ({ ...p, contractor: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>Contact Phone</Label>
          <Input value={form.contact_phone} onChange={(e) => setForm(p => ({ ...p, contact_phone: e.target.value }))} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Scope of Work</Label>
        <Textarea value={form.scope_of_work} onChange={(e) => setForm(p => ({ ...p, scope_of_work: e.target.value }))} rows={2} />
      </div>

      <div className="space-y-2">
        <Label>Utility Conflicts Identified</Label>
        <Textarea value={form.utility_conflicts} onChange={(e) => setForm(p => ({ ...p, utility_conflicts: e.target.value }))} rows={2} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Access Issues</Label>
          <Textarea value={form.access_issues} onChange={(e) => setForm(p => ({ ...p, access_issues: e.target.value }))} rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Safety Concerns</Label>
          <Textarea value={form.safety_concerns} onChange={(e) => setForm(p => ({ ...p, safety_concerns: e.target.value }))} rows={2} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Special Requirements</Label>
        <Textarea value={form.special_requirements} onChange={(e) => setForm(p => ({ ...p, special_requirements: e.target.value }))} rows={2} />
      </div>

      <div className="space-y-2">
        <Label>Meeting Attendees</Label>
        <Input value={form.attendees} onChange={(e) => setForm(p => ({ ...p, attendees: e.target.value }))} placeholder="Names separated by commas" />
      </div>

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} />
      </div>

      <Button onClick={handleSubmit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900">
        <Download className="w-4 h-4 mr-2" />Generate PreCon Form
      </Button>
    </div>
  );
}

// Locate Report Form
function LocateReportForm({ jobs, onGenerate }) {
  const [form, setForm] = useState({
    job_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    ticket_number: '',
    locate_type: 'Standard',
    utilities_marked: [],
    marking_method: '',
    accuracy: '',
    conflicts_found: false,
    conflict_description: '',
    photos_taken: false,
    sketch_completed: false,
    completion_time: '',
    technician: '',
    notes: ''
  });

  const utilityTypes = ['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Fiber', 'CATV', 'Storm Drain'];

  const handleUtilityToggle = (utility) => {
    setForm(prev => ({
      ...prev,
      utilities_marked: prev.utilities_marked.includes(utility)
        ? prev.utilities_marked.filter(u => u !== utility)
        : [...prev.utilities_marked, utility]
    }));
  };

  const handleSubmit = () => {
    if (!form.job_id || !form.ticket_number) {
      toast.error('Please fill required fields');
      return;
    }
    const selectedJob = jobs.find(j => j.id === form.job_id);
    onGenerate({ ...form, job: selectedJob });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Job *</Label>
          <Select value={form.job_id} onValueChange={(v) => setForm(p => ({ ...p, job_id: v }))}>
            <SelectTrigger><SelectValue placeholder="Select job" /></SelectTrigger>
            <SelectContent>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>{job.job_title} - {job.client_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Ticket Number *</Label>
          <Input value={form.ticket_number} onChange={(e) => setForm(p => ({ ...p, ticket_number: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>Locate Type</Label>
          <Select value={form.locate_type} onValueChange={(v) => setForm(p => ({ ...p, locate_type: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Standard">Standard</SelectItem>
              <SelectItem value="Emergency">Emergency</SelectItem>
              <SelectItem value="Design">Design</SelectItem>
              <SelectItem value="Re-mark">Re-mark</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Utilities Marked</Label>
        <div className="flex flex-wrap gap-3">
          {utilityTypes.map(util => (
            <label key={util} className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.utilities_marked.includes(util)} onCheckedChange={() => handleUtilityToggle(util)} />
              <span className="text-sm">{util}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Marking Method</Label>
          <Select value={form.marking_method} onValueChange={(v) => setForm(p => ({ ...p, marking_method: v }))}>
            <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Paint">Paint</SelectItem>
              <SelectItem value="Flags">Flags</SelectItem>
              <SelectItem value="Paint & Flags">Paint & Flags</SelectItem>
              <SelectItem value="Stakes">Stakes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estimated Accuracy</Label>
          <Select value={form.accuracy} onValueChange={(v) => setForm(p => ({ ...p, accuracy: v }))}>
            <SelectTrigger><SelectValue placeholder="Select accuracy" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="±6 inches">±6 inches</SelectItem>
              <SelectItem value="±12 inches">±12 inches</SelectItem>
              <SelectItem value="±18 inches">±18 inches</SelectItem>
              <SelectItem value="±24 inches">±24 inches</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-6 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.conflicts_found} onCheckedChange={(c) => setForm(p => ({ ...p, conflicts_found: c }))} />
          <span className="text-sm">Conflicts Found</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.photos_taken} onCheckedChange={(c) => setForm(p => ({ ...p, photos_taken: c }))} />
          <span className="text-sm">Photos Taken</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.sketch_completed} onCheckedChange={(c) => setForm(p => ({ ...p, sketch_completed: c }))} />
          <span className="text-sm">Sketch Completed</span>
        </label>
      </div>

      {form.conflicts_found && (
        <div className="space-y-2">
          <Label>Conflict Description</Label>
          <Textarea value={form.conflict_description} onChange={(e) => setForm(p => ({ ...p, conflict_description: e.target.value }))} rows={2} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Completion Time</Label>
          <TimeInput value={form.completion_time} onChange={(val) => setForm(p => ({ ...p, completion_time: val }))} />
        </div>
        <div className="space-y-2">
          <Label>Technician Name</Label>
          <Input value={form.technician} onChange={(e) => setForm(p => ({ ...p, technician: e.target.value }))} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} />
      </div>

      <Button onClick={handleSubmit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900">
        <Download className="w-4 h-4 mr-2" />Generate Locate Report
      </Button>
    </div>
  );
}

// PDF Generation Functions
function generateSoftDigPDF(data) {
  const html = `
    <!DOCTYPE html><html><head><title>Soft Dig Data Sheet</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
      .header { text-align: center; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; margin-bottom: 25px; }
      .header h1 { margin: 0; color: #1e293b; }
      .header p { margin: 5px 0 0; color: #64748b; }
      .section { margin-bottom: 20px; }
      .section-title { font-weight: bold; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
      .row { display: flex; margin-bottom: 8px; }
      .label { width: 150px; font-weight: 600; color: #64748b; }
      .value { flex: 1; color: #1e293b; }
      .checkbox { display: inline-block; width: 14px; height: 14px; border: 1px solid #000; margin-right: 5px; text-align: center; line-height: 12px; }
      .checked { background: #f59e0b; }
      .utilities { display: flex; flex-wrap: wrap; gap: 10px; }
      .signature { margin-top: 50px; display: flex; justify-content: space-between; }
      .sig-line { width: 45%; border-top: 1px solid #000; padding-top: 5px; font-size: 12px; color: #64748b; }
    </style></head><body>
    <div class="header"><h1>CENTER LINE LOCATING</h1><p>Soft Dig Data Sheet</p></div>
    <div class="section">
      <div class="section-title">Job Information</div>
      <div class="row"><span class="label">Job:</span><span class="value">${data.job?.job_title || 'N/A'} - ${data.job?.client_name || ''}</span></div>
      <div class="row"><span class="label">Date:</span><span class="value">${format(parseLocalDate(data.date), 'MMMM d, yyyy')}</span></div>
      <div class="row"><span class="label">Location:</span><span class="value">${data.job?.service_address || 'N/A'}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Dig Details</div>
      <div class="row"><span class="label">Dig Location:</span><span class="value">${data.location}</span></div>
      <div class="row"><span class="label">Depth Reached:</span><span class="value">${data.depth || 'N/A'}</span></div>
      <div class="row"><span class="label">Soil Type:</span><span class="value">${data.soil_type || 'N/A'}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Utilities Found</div>
      <div class="utilities">
        ${['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Fiber', 'Unknown'].map(u => 
          `<span><span class="checkbox ${data.utilities_found.includes(u) ? 'checked' : ''}">${data.utilities_found.includes(u) ? '✓' : ''}</span>${u}</span>`
        ).join('')}
      </div>
    </div>
    <div class="section">
      <div class="section-title">Completion</div>
      <div class="row"><span class="label">Photos Taken:</span><span class="value">${data.photos_taken ? 'Yes' : 'No'}</span></div>
      <div class="row"><span class="label">Ground Restored:</span><span class="value">${data.ground_restored ? 'Yes' : 'No'}</span></div>
      <div class="row"><span class="label">Technician:</span><span class="value">${data.technician || 'N/A'}</span></div>
      ${data.notes ? `<div class="row"><span class="label">Notes:</span><span class="value">${data.notes}</span></div>` : ''}
    </div>
    <div class="signature">
      <div class="sig-line">Technician Signature</div>
      <div class="sig-line">Date</div>
    </div>
    </body></html>
  `;
  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 300); }
}

function generatePreConPDF(data) {
  const html = `
    <!DOCTYPE html><html><head><title>Pre-Construction Meeting Form</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
      .header { text-align: center; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; margin-bottom: 25px; }
      .header h1 { margin: 0; color: #1e293b; }
      .header p { margin: 5px 0 0; color: #64748b; }
      .section { margin-bottom: 20px; }
      .section-title { font-weight: bold; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
      .row { display: flex; margin-bottom: 8px; }
      .label { width: 150px; font-weight: 600; color: #64748b; }
      .value { flex: 1; color: #1e293b; }
      .text-block { background: #f8fafc; padding: 10px; border-radius: 4px; margin-top: 5px; }
      .signature { margin-top: 50px; display: flex; justify-content: space-between; }
      .sig-line { width: 30%; border-top: 1px solid #000; padding-top: 5px; font-size: 12px; color: #64748b; }
    </style></head><body>
    <div class="header"><h1>CENTER LINE LOCATING</h1><p>Pre-Construction Meeting Form</p></div>
    <div class="section">
      <div class="section-title">Project Information</div>
      <div class="row"><span class="label">Project Name:</span><span class="value">${data.project_name}</span></div>
      <div class="row"><span class="label">Job Reference:</span><span class="value">${data.job?.job_title || 'N/A'}</span></div>
      <div class="row"><span class="label">Date:</span><span class="value">${format(parseLocalDate(data.date), 'MMMM d, yyyy')}</span></div>
      <div class="row"><span class="label">Location:</span><span class="value">${data.job?.service_address || 'N/A'}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Contractor Information</div>
      <div class="row"><span class="label">Contractor:</span><span class="value">${data.contractor || 'N/A'}</span></div>
      <div class="row"><span class="label">Contact Phone:</span><span class="value">${data.contact_phone || 'N/A'}</span></div>
      <div class="row"><span class="label">Attendees:</span><span class="value">${data.attendees || 'N/A'}</span></div>
    </div>
    ${data.scope_of_work ? `<div class="section"><div class="section-title">Scope of Work</div><div class="text-block">${data.scope_of_work}</div></div>` : ''}
    ${data.utility_conflicts ? `<div class="section"><div class="section-title">Utility Conflicts Identified</div><div class="text-block">${data.utility_conflicts}</div></div>` : ''}
    ${data.access_issues ? `<div class="section"><div class="section-title">Access Issues</div><div class="text-block">${data.access_issues}</div></div>` : ''}
    ${data.safety_concerns ? `<div class="section"><div class="section-title">Safety Concerns</div><div class="text-block">${data.safety_concerns}</div></div>` : ''}
    ${data.special_requirements ? `<div class="section"><div class="section-title">Special Requirements</div><div class="text-block">${data.special_requirements}</div></div>` : ''}
    ${data.notes ? `<div class="section"><div class="section-title">Additional Notes</div><div class="text-block">${data.notes}</div></div>` : ''}
    <div class="signature">
      <div class="sig-line">Center Line Locating Representative</div>
      <div class="sig-line">Contractor Representative</div>
      <div class="sig-line">Date</div>
    </div>
    </body></html>
  `;
  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 300); }
}

function generateLocateReportPDF(data) {
  const html = `
    <!DOCTYPE html><html><head><title>Locate Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
      .header { text-align: center; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; margin-bottom: 25px; }
      .header h1 { margin: 0; color: #1e293b; }
      .header p { margin: 5px 0 0; color: #64748b; }
      .section { margin-bottom: 20px; }
      .section-title { font-weight: bold; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
      .row { display: flex; margin-bottom: 8px; }
      .label { width: 150px; font-weight: 600; color: #64748b; }
      .value { flex: 1; color: #1e293b; }
      .checkbox { display: inline-block; width: 14px; height: 14px; border: 1px solid #000; margin-right: 5px; text-align: center; line-height: 12px; }
      .checked { background: #f59e0b; }
      .utilities { display: flex; flex-wrap: wrap; gap: 10px; }
      .alert { background: #fef2f2; border: 1px solid #fecaca; padding: 10px; border-radius: 4px; color: #dc2626; }
      .signature { margin-top: 50px; display: flex; justify-content: space-between; }
      .sig-line { width: 45%; border-top: 1px solid #000; padding-top: 5px; font-size: 12px; color: #64748b; }
    </style></head><body>
    <div class="header"><h1>CENTER LINE LOCATING</h1><p>Utility Locate Report</p></div>
    <div class="section">
      <div class="section-title">Locate Information</div>
      <div class="row"><span class="label">Ticket Number:</span><span class="value"><strong>${data.ticket_number}</strong></span></div>
      <div class="row"><span class="label">Locate Type:</span><span class="value">${data.locate_type}</span></div>
      <div class="row"><span class="label">Date:</span><span class="value">${format(parseLocalDate(data.date), 'MMMM d, yyyy')}</span></div>
      <div class="row"><span class="label">Job Reference:</span><span class="value">${data.job?.job_title || 'N/A'}</span></div>
      <div class="row"><span class="label">Location:</span><span class="value">${data.job?.service_address || 'N/A'}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Utilities Marked</div>
      <div class="utilities">
        ${['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Fiber', 'CATV', 'Storm Drain'].map(u => 
          `<span><span class="checkbox ${data.utilities_marked.includes(u) ? 'checked' : ''}">${data.utilities_marked.includes(u) ? '✓' : ''}</span>${u}</span>`
        ).join('')}
      </div>
    </div>
    <div class="section">
      <div class="section-title">Marking Details</div>
      <div class="row"><span class="label">Marking Method:</span><span class="value">${data.marking_method || 'N/A'}</span></div>
      <div class="row"><span class="label">Accuracy:</span><span class="value">${data.accuracy || 'N/A'}</span></div>
    </div>
    ${data.conflicts_found ? `<div class="section"><div class="section-title">⚠️ Conflicts Found</div><div class="alert">${data.conflict_description || 'Conflicts identified - see notes'}</div></div>` : ''}
    <div class="section">
      <div class="section-title">Completion</div>
      <div class="row"><span class="label">Photos Taken:</span><span class="value">${data.photos_taken ? 'Yes' : 'No'}</span></div>
      <div class="row"><span class="label">Sketch Completed:</span><span class="value">${data.sketch_completed ? 'Yes' : 'No'}</span></div>
      <div class="row"><span class="label">Completion Time:</span><span class="value">${data.completion_time || 'N/A'}</span></div>
      <div class="row"><span class="label">Technician:</span><span class="value">${data.technician || 'N/A'}</span></div>
      ${data.notes ? `<div class="row"><span class="label">Notes:</span><span class="value">${data.notes}</span></div>` : ''}
    </div>
    <div class="signature">
      <div class="sig-line">Technician Signature</div>
      <div class="sig-line">Date</div>
    </div>
    </body></html>
  `;
  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 300); }
}

// Main Component
export default function ReportForms() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 animate-pulse rounded" />
        <div className="h-96 bg-slate-200 animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-forms-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Report Forms
          </h1>
          <p className="text-slate-500 mt-1">Generate specialized field reports</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <Tabs defaultValue="softdig" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="softdig" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                <FileText className="w-4 h-4 mr-2" />Soft Dig Data Sheet
              </TabsTrigger>
              <TabsTrigger value="precon" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                <FileText className="w-4 h-4 mr-2" />PreCon Form
              </TabsTrigger>
              <TabsTrigger value="locate" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                <FileText className="w-4 h-4 mr-2" />Locate Report
              </TabsTrigger>
            </TabsList>

            <TabsContent value="softdig">
              <SoftDigForm jobs={jobs} onGenerate={generateSoftDigPDF} />
            </TabsContent>

            <TabsContent value="precon">
              <PreConForm jobs={jobs} onGenerate={generatePreConPDF} />
            </TabsContent>

            <TabsContent value="locate">
              <LocateReportForm jobs={jobs} onGenerate={generateLocateReportPDF} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
